import { useNavigate } from 'react-router-dom';
import * as z from 'zod';

import { Button, Spinner } from '@/components/Elements';
import { Form, InputField } from '@/components/Form';
import { BetaCountrySelect } from '@/features/misc';
import { useAuth } from '@/lib/auth';

import { CreateDTO, useCreate } from '../../api/create';
import { useInformation } from '../../api/getInformation';
import { State } from '../State';

const personalInformationSchema = z.object({
  firstName: z.string().trim().min(1, 'Required'),
  middleName: z.string().trim().optional(),
  lastName: z.string().trim().min(1, 'Required'),
  phoneNumber: z.string().trim().min(1, 'Required'),
  birthdate: z.string().trim().min(1, 'Required'),
  nationality: z.string().trim().min(1, 'Required'),
});

const personalIdsSchema = z.object({
  socialSecurityId: z.string().trim().min(1, 'Required'),
  workPermitId: z.string().trim().min(1, 'Required').optional(),
  federal: z.string().trim().min(1, 'Required'),
  provincial: z.string().trim().min(1, 'Required'),
  identityId: z.string().trim().min(1, 'Required'),
});

const addressSchema = z.object({
  street: z.string().trim().min(1, 'Required'),
  city: z.string().trim().min(1, 'Required'),
  state: z.string().trim().min(1, 'Required'),
  postalCode: z.string().trim().min(1, 'Required'),
  countryCode: z.string().trim().min(1, 'Required'),
});

const caPersonalInformationSchema = z.object({
  ...personalInformationSchema.shape,
  ...personalIdsSchema.shape,
  ...addressSchema.shape,
});

export type TCaPersonalInformationSchema = z.infer<typeof caPersonalInformationSchema>;

const createDTO = (values: TCaPersonalInformationSchema): CreateDTO => {
  return {
    payee: {
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      birthdate: values.birthdate,
      nationality: values.nationality,
    },
    address: {
      street: values.street,
      city: values.city,
      state: values.state,
      postalCode: values.postalCode,
      countryCode: values.countryCode,
    },
    personalIds: {
      identityId: values.identityId,
      socialSecurityId: values.socialSecurityId,
      totalClaimAmount: {
        federal: values.federal,
        provincial: values.provincial,
      },
      workPermitId: values.workPermitId,
    },
  };
};

export const CaPersonalInformationForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const onboardingInformationQuery = useInformation();
  const createMutation = useCreate();

  const handleOnSubmit = async (values: TCaPersonalInformationSchema) => {
    await createMutation.mutateAsync(createDTO(values), {
      onSettled: (data) => {
        if (data) navigate('/onboarding/banking');
      },
    });
  };

  if (onboardingInformationQuery.isLoading)
    return (
      <div className="flex h-48 w-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );

  const offerCountryCode = user?.data.config.offerCountryCode;
  const onboardingInformation = onboardingInformationQuery.data?.data;

  const defaultValues: Partial<TCaPersonalInformationSchema> = {
    ...onboardingInformation?.address,
    ...onboardingInformation?.payee,
    ...onboardingInformation?.personalIds,
    ...onboardingInformation?.personalIds.totalClaimAmount,
  };

  return (
    <Form<TCaPersonalInformationSchema, typeof caPersonalInformationSchema>
      onSubmit={async (payee) => {
        handleOnSubmit(payee);
      }}
      schema={caPersonalInformationSchema}
      className="space-y-8 divide-y divide-gray-200"
      options={{
        defaultValues,
        shouldUnregister: true,
      }}
    >
      {({ formState, register, resetField, watch }) => {
        const nationality = watch('nationality');
        const isNational = nationality === offerCountryCode;

        return (
          <div className="space-y-8 divide-y divide-gray-200">
            <div>
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Personal Information
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Please fill in your personal details as they appear on your government issued ID.
                </p>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <InputField
                    label="First name"
                    error={formState.errors['firstName']}
                    registration={register('firstName')}
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Middle name (optional)"
                    error={formState.errors['middleName']}
                    registration={register('middleName')}
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Last name"
                    error={formState.errors['lastName']}
                    registration={register('lastName')}
                    helperText="Exactly as it appears on your government issued ID"
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Phone number"
                    error={formState.errors['phoneNumber']}
                    registration={register('phoneNumber')}
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Birth date"
                    type="date"
                    error={formState.errors['birthdate']}
                    registration={register('birthdate')}
                  />
                </div>
                <div className="sm:col-span-4">
                  <BetaCountrySelect
                    label="Nationality"
                    error={formState.errors['nationality']}
                    registration={register('nationality')}
                  />
                </div>
              </div>
            </div>
            <div className="pt-8">
              <div>
                <p className="mt-1 text-sm text-gray-500">
                  Please fill in your personal ID information.
                </p>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <InputField
                    label="Social security number"
                    error={formState.errors['socialSecurityId']}
                    registration={register('socialSecurityId')}
                    helperText="SIN / Employee ID"
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Document ID number"
                    error={formState.errors['identityId']}
                    registration={register('identityId')}
                    helperText="Passport, others"
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Federal total claim amount"
                    error={formState.errors['federal']}
                    registration={register('federal')}
                    helperText={
                      <a
                        href={
                          'https://s3.amazonaws.com/via-bucket.api.via.work/tax-forms/td1-22e.pdf'
                        }
                        className="underline focus:underline focus:underline-offset-2 focus:outline-none"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Download tax form
                      </a>
                    }
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Provincial total claim amount"
                    error={formState.errors['provincial']}
                    registration={register('provincial')}
                    helperText={
                      <a
                        href={
                          'https://s3.amazonaws.com/via-bucket.api.via.work/tax-forms/td1bc-22e.pdf'
                        }
                        className="underline focus:underline focus:underline-offset-2 focus:outline-none"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Download tax form
                      </a>
                    }
                  />
                </div>
                {!isNational && (
                  <div className="sm:col-span-2">
                    <InputField
                      label="Work permit number"
                      error={formState.errors['workPermitId']}
                      registration={register('workPermitId')}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="pt-8">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">Address</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Please enter the address you will be working from.
                </p>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-4">
                  <InputField
                    label="Street"
                    error={formState.errors['street']}
                    registration={register('street')}
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="City"
                    error={formState.errors['city']}
                    registration={register('city')}
                  />
                </div>
                <div className="sm:col-span-2">
                  <State countryCode={watch('countryCode')} />
                </div>
                <div className="sm:col-span-1">
                  <InputField
                    label="Postal code"
                    error={formState.errors['postalCode']}
                    registration={register('postalCode')}
                  />
                </div>
                <div className="sm:col-span-3">
                  <BetaCountrySelect
                    label="Country"
                    error={formState.errors['countryCode']}
                    registration={register('countryCode', {
                      onChange: () => {
                        resetField('state', { defaultValue: '' });
                      },
                    })}
                  />
                </div>
              </div>
            </div>
            <div className="pt-5">
              <div className="flex justify-end">
                <Button
                  isLoading={createMutation.isLoading}
                  disabled={createMutation.isLoading}
                  type="submit"
                  size="lg"
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        );
      }}
    </Form>
  );
};
